import { Select as AriaKitSelect, useSelectState as useAriaKitSelectState } from "ariakit";
import React from "react";
import styled from "styled-components";
import { useEvent } from "../hooks/useEvent";
import { PropOption } from "../PropX";
import { SelectPopover } from "../SelectPopover_v2";
import { style, useGetBoundingClientRectRef } from "../style";

type Props = {
  /**
   * The list of options to display in the select menu. For performance,
   * make sure that this value is memoized between renders.
   */
  options: PropOption[];
  /**
   * Whether the select menu is disabled. If disabled, the select menu will close.
   * @default false
   */
  disabled?: boolean;
  /**
   * Sets a minimum width on the select menu. Otherwise, will only be as wide as the widest option.
   * When "button" is selected, the select menu will be as wide as the button.
   * @default "auto"
   */
  width?: "medium" | "large" | "auto" | "button";
  /**
   * Accepts a function as a child which is the clickable element to open the select menu.
   */
  children(params: { open: boolean; disabled: boolean }): React.ReactNode;
  /**
   * The name of the radio group.
   */
  name: string;
  /**
   * The currently selected value.
   */
  selected: string | null;
  /**
   * A callback to set the selected value.
   */
  onSelect(value: string | null): void;
  /**
   * Whether the menu should be closed on pressing escape.
   * @default true
   */
  closeOnEscape?: boolean;
  /**
   * Whether the menu items should display as compacted elements.
   */
  compact?: boolean;
  /**
   * A search term to filter options by.
   * @default ""
   */
  search?: string;

  /**
   * An optional header to display above the list of options.
   * @default null
   */
  listHeader?: React.ReactNode;

  /**
   * An optional footer to display below the list of options.
   * @default null
   */
  listFooter?: React.ReactNode;

  /**
   * Used to shift X position of the dropdown menu relative to the button being clicked.
   * @private
   * @default 0
   */
  UNSAFE_relativeShiftX?: number;

  /**
   * Used to shift Y position of the dropdown menu relative to the button being clicked.
   * @private
   * @default 0
   */
  UNSAFE_relativeShiftY?: number;

  /**
   * The number of items to render above and below the visible items in the list.
   * @private
   * @default 10
   */
  UNSAFE_overscanCount?: number;

  /**
   * Dynamically calculates the height of each item in the list. Necessary when items have variable heights.
   * WARNING: This is very slow and should only be used when absolutely necessary with a short list of items.
   * Should not be used in scenarios where the list of options is long or frequently changing.
   * @private
   * @default false
   */
  UNSAFE_precalculateItemHeights?: boolean;

  /**
   * A callback used to set the data-testid on elements of the select menu.
   * @default (x) => x
   */
  "data-testid"?: (value: string) => string;
};

type RefHandle = {
  show(): void;
  hide(): void;
  toggle(): void;
  open: boolean;
};

const SelectButtonWrapper = styled(AriaKitSelect)`
  all: unset;
  box-sizing: border-box;
  height: 100%;
  width: 100%;

  :focus {
    border-radius: ${style.vars.borderRadius.base};
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: ${style.vars.borderWidth.thin};
  }
`;

const identity = (value: string) => value;

export const HeadlessSelectMenu = React.forwardRef<RefHandle, Props>((props, ref) => {
  const {
    name,
    onSelect,
    selected,
    children,
    options,
    listHeader = null,
    listFooter = null,
    disabled = false,
    closeOnEscape = true,
    width = "auto",
    search = "",
    compact,
    UNSAFE_relativeShiftX = 0,
    UNSAFE_relativeShiftY = 0,
    UNSAFE_overscanCount = 10,
    UNSAFE_precalculateItemHeights = false,
  } = props;

  const testIdFn = props["data-testid"] ?? identity;

  const option = selected === null ? undefined : options.find((o) => o.value === selected);

  /**
   * Group the options knowing that there can only be, at most, one selected option.
   */
  const bin = React.useMemo(() => {
    const selectedTuple = option ? [option] : [];

    return PropOption.bin(options, selectedTuple, { filterSearch: search, filterSelectedFromItems: false });
  }, [options, option, search]);

  const state = useAriaKitSelectState<string>({
    placement: "bottom-end",
    gutter: UNSAFE_relativeShiftY,
    shift: UNSAFE_relativeShiftX,
    defaultValue: options[0]?.value,
    value: option?.value ?? undefined,
    setValue(key: string) {
      const option = PropOption.fullMatchBySearch(options, key);

      if (option) {
        onSelect(option.value);
      }
    },
    virtualFocus: false,
    defaultActiveId: option ? `${name}-${option.value}-select-menu-item` : undefined,
  });

  React.useImperativeHandle(ref, () => {
    return {
      show: () => {
        if (!state.open && !disabled) state.show();
      },
      hide: () => {
        if (state.open && !disabled) state.hide();
      },
      toggle: () => {
        if (!disabled) state.toggle();
      },
      open: state.open,
    };
  });

  /**
   * When the menu is disabled, hide it so that it doesn't stay open and unclickable.
   */
  React.useEffect(() => {
    if (state.open && disabled) {
      state.hide();
    }
  }, [state.open, disabled]);

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const getBoundingClientRect = useGetBoundingClientRectRef();

  const menuWidth = React.useMemo(() => {
    if (width === "button") {
      return () => {
        const width = getBoundingClientRect(buttonRef).width;

        return width > 0 ? `${width}px` : "auto";
      };
    }

    return width;
  }, [width, getBoundingClientRect, buttonRef.current, bin.items]);

  /**
   * This is a hack to get around a bug in ariakit where the internal focus
   * event is not persisting the React event object. This causes React to release
   * the event and throw an error when it is accessed.
   */
  const handleFocus = useEvent<React.FocusEventHandler<HTMLElement>>((ev) => {
    ev.persist();
  });

  const handleKeyDown = useEvent<React.KeyboardEventHandler<HTMLElement>>((ev) => {
    ev.persist();
  });

  return (
    <>
      <SelectButtonWrapper
        ref={buttonRef}
        state={state}
        id={`${name}-select-menu-button`}
        disabled={disabled}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        data-testid={name}
      >
        {children({ open: state.open, disabled: disabled })}
      </SelectButtonWrapper>
      <SelectPopover
        state={state}
        id={`${name}-select-menu`}
        width={menuWidth}
        closeOnEscape={closeOnEscape}
        listHeader={listHeader}
        listFooter={listFooter}
        autoFocusOnShow
        autoFocusOnHide
        items={bin.items}
        compact={compact}
        UNSAFE_overscanCount={UNSAFE_overscanCount}
        UNSAFE_precalculateItemHeights={UNSAFE_precalculateItemHeights}
        data-testid={testIdFn}
      />
    </>
  );
});

HeadlessSelectMenu.displayName = "HeadlessSelectMenu";
